import ValidateModule from "./ValidateModule";

import type { IEventChannel, ApiOpts } from "../model/Models";
import type AjaxRequestBroker from "../AjaxRequestBroker";
import type CacheModule from "./CacheModule";

const es = global.es;

/**
 * @class Client side projections only support Spherical mercator at present - for more use Proj4js
 */
export default class ProjectionModule {
    private eventChannel: IEventChannel;
    private rqBroker: AjaxRequestBroker;
    private resourceName: string;
    private cache: CacheModule;
    
    /**
     * 
     * @param eventChannel 
     * @param rqBroker 
     * @param resourceName 
     * @param cache 
     */
    constructor(eventChannel: IEventChannel, rqBroker: AjaxRequestBroker, resourceName: string, cache: CacheModule) {
        this.eventChannel = eventChannel;
        this.rqBroker = rqBroker;
        this.resourceName = resourceName;
        this.cache = cache;

        // TODO FIXME Does this even make sense nowadays?
        eventChannel.addListener("onDestroy", () => {
            // prevent memory leaks when the map widget is destroyed
            this.eventChannel = this.rqBroker = this.resourceName = this.cache = null;
        });

        this.get = this.get.bind(this);
        this.getProjectionsSystems = this.getProjectionsSystems.bind(this);
        this.transform = this.transform.bind(this);
        this.getWrappedBounds = this.getWrappedBounds.bind(this);
        this.calculateScaleFactorAt = this.calculateScaleFactorAt.bind(this);
    }

    /**
     * Get the resource from the server
     * 
     * @param id
     * @param opts
     * 
     * @returns a jquery deferred object resolved when the projections are loaded
     */
    get(id: number, opts?: ApiOpts) {
        if (!ValidateModule.checkId(id)) {
            return es.util.deferred.rejected();
        }
        
        const url = es.api.getURL(this.resourceName) + "/" + id;
        return this.cache.doGet(this.rqBroker, this.resourceName, url, id, opts);
    }
    
    /**
     * Get the resource from the server
     * 
     * @param searchText
     * @param opts
     * 
     * @return a jquery deferred object resolved when the projections are loaded
     */
    getProjectionsSystems(searchText: string, opts?: ApiOpts) {
        let url = es.api.getURL(this.resourceName);
        if (searchText) {
            url += '/' + searchText;
        }
        
        //return rqBroker.doGet({url: url});
        return this.cache.doGet(this.rqBroker, this.resourceName, url, "getProjectionsSystems_" + (searchText ? searchText : "list"), opts);
    }
    
    /**
     * 
     * @param srcId 
     * @param dstId 
     * @param array 
     * @param offset 
     * @param length 
     * @param resultArray 
     * 
     * @returns 
     * 
     * @deprecated TO BE REMOVED!!!! DO NOT USE IT!!!!
     */
    transform(srcId: number, dstId: number, array: number[], offset?: number, length?: number, resultArray?: number[]) {
        if (!offset) {
            offset = 0;
        }
        
        if (!length) {
            length = array.length;
        }

        if (!resultArray) {
            resultArray = [];
        }

        const endIndex = offset + length;
        if (srcId == dstId) {
            for (let i = offset; i < endIndex;) {
                resultArray.push(array[i++], array[i++]);
            }

            return es.util.deferred.resolved(resultArray);
        }
        
        if ((srcId == 3857) && (dstId == 4326)) { // mercator to lat lon
            resultArray = mercator2LonLat(array, offset, length, resultArray);
            return es.util.deferred.resolved(resultArray);
        }
        
        if ((srcId == 4326) && (dstId == 3857)) { // lat lon to mercator
            resultArray = lonLat2Mercator(array, offset, length, resultArray);
            return es.util.deferred.resolved(resultArray);
        }
        
        throw new Error("This method will be removed and should be only used for lonLat/mercator conversions for now...");
    }
    
    /**
     * 
     * @param viewBounds 
     * @param projection 
     * 
     * @returns 
     */
    getWrappedBounds(viewBounds, projection) {
        const maxBounds = projection.maxBounds;
        viewBounds = [
            projection.loopX ? viewBounds[0] : Math.max(viewBounds[0], maxBounds[0]),
            /*projection.loopY ? viewBounds[1] :*/ Math.max(viewBounds[1], maxBounds[1]),
            projection.loopX ? viewBounds[2] : Math.min(viewBounds[2], maxBounds[2]),
            /*projection.loopY ? viewBounds[3] :*/ Math.min(viewBounds[3], maxBounds[3])
        ];

        const maxWidth = (maxBounds[2] - maxBounds[0]);
        const maxHeight = (maxBounds[3] - maxBounds[1]);
        const minIndexX = Math.floor((viewBounds[0] - maxBounds[0]) / maxWidth);
        const minIndexY = Math.floor((viewBounds[1] - maxBounds[1]) / maxHeight);
        const maxIndexX = Math.ceil((viewBounds[2] - maxBounds[0]) / maxWidth);
        const maxIndexY = Math.ceil((viewBounds[3] - maxBounds[1]) / maxHeight);

        const results = [];
        for (let x = minIndexX; x < maxIndexX; x++) {
            let dx = x * (maxBounds[2] - maxBounds[0]);
            if (dx) {
                dx = -dx;
            }

            let minX = viewBounds[0] + dx;
            let maxX = viewBounds[2] + dx;

            if ((minX < maxBounds[0]) || (minX > maxBounds[2])) {
                minX = maxBounds[0];
            }

            if ((maxX < maxBounds[0]) || (maxX > maxBounds[2])) {
                maxX = maxBounds[2];
            }

            for (let y = minIndexY; y < maxIndexY; y++) {
                let dy = y * (maxBounds[2] - maxBounds[0]);

                if (dy) {
                    dy = -dy;
                }

                let minY = viewBounds[1] + dy;
                let maxY = viewBounds[3] + dy;

                if ((minY < maxBounds[1]) || (minY > maxBounds[3])) {
                    minY = maxBounds[1];
                }

                if ((maxY < maxBounds[1]) || (maxY > maxBounds[3])) {
                    maxY = maxBounds[3];
                }
                
                results.push({
                    bounds: [minX, minY, maxX, maxY],
                    dx: dx,
                    dy: dy
                });
            }
        }

        return results;
    }
    
    /**
     * Get the scale factor to apply at the position given for the projection given
     * 
     * @param projection
     * @param x
     * @param y
     * 
     * @returns
     */
    calculateScaleFactorAt(projection, x, y) {
        if (projection.lonLat) {
            return es.util.deferred.resolved(scaleFactorForLat(y));  // formula for scaling on a lon lat mercator map
        }
        
        if (projection.id === 3857) {
            return this.transform(projection.id, 4326, [x, y]).then((pnt) => {
                return scaleFactorForLat(pnt[1]);
            });
        }
        
        return es.util.deferred.rejected("UnsupportedOperation");
    }
}

/**
 * Converts Mercator coordinates to Longitude/Latitude.
 * @param array
 * @param offset
 * @param length
 * @param resultArray
 * 
 * @returns
 */
function mercator2LonLat(array: number[], offset:number, length:number, resultArray: number[]): number[] {
    checkCoords(array, "MERCATOR");

    if (!offset) {
        offset = 0;
    }

    if (!length) {
        length = array.length;
    }

    if (!resultArray) {
        resultArray = [];
    }

    const endIndex = offset + length;
    for (let i = offset; i < endIndex;) {
        resultArray.push(x2lon(array[i++]), y2lat(array[i++]));
    }

    return resultArray;
}

/**
 * Converts Longitude/Latitude coordinates to mercator
 * @param array
 * @param offset
 * @param length
 * @param resultArray
 * 
 * @returns
 */
function lonLat2Mercator(array: number[], offset: number, length: number, resultArray: number[]) {
    checkCoords(array, "LATLON");

    if (!offset) {
        offset = 0;
    }

    if (!length) {
        length = array.length;
    }

    if (!resultArray) {
        resultArray = [];
    }

    const endIndex = offset + length;
    for (let i = offset; i < endIndex; ) {
        const lon = lon2x(array[i++]);
        const lat = lat2y(array[i++]);

        if (isNaN(lon) || isNaN(lat)) {
            throw new Error("lonLat2Mercator error causing NaN result: " + JSON.stringify(array));
        }

        resultArray.push(lon, lat);
    }

    return resultArray;
}

/**
 * 
 * @param lat 
 * 
 * @returns 
 */
function scaleFactorForLat(lat: number): number {
    return 1 / Math.cos(lat * Math.PI / 180);
}

/**
 * 
 * @param x 
 * 
 * @returns 
 */
function x2lon(x: number): number {
    x = x * 180 / 20037508.3427892;
    return x;
    // return round(x, 7);
}

/**
 * 
 * @param lon 
 * 
 * @returns 
 */
function lon2x(lon:number): number | null {
    lon  = Number(lon);
    if(isNaN(lon)) {
        return null;
    }
    return lon * 20037508.3427892 / 180;
}

/**
 * 
 * @param y 
 * 
 * @returns 
 */
function y2lat(y: number): number {
    y = (y / 20037508.3427892) * 180;
    y = 180 / Math.PI * (2 * Math.atan(Math.exp(y * Math.PI / 180)) - Math.PI / 2);
    return y;
    // return round(y, 7);
}

/**
 * 
 * @param lat 
 * 
 * @returns 
 */
function lat2y(lat: number): number | null {
    lat  = Number(lat);
    if (isNaN(lat)) {
        return null;
    }

    //let y = 180 / Math.PI * (2 * Math.atan(Math.exp((lat / 20037508.3427892) * Math.PI)) - Math.PI / 2);
    let y = Math.log(Math.tan((90 + lat) * Math.PI / 360)) / Math.PI * 20037508.3427892;
    y = Math.max(-20037508.3427892, Math.min(y, 20037508.3427892));

    return y;
}

/**
 * Log a warning if suspect coords are not in expected proj system.
 * @param {[number, number]} coords
 * @param {"LATLON" | "MERCATOR"} expectedSys
 */
function checkCoords(coords: number[], expectedSys: string) {
    if (Array.isArray(coords[0]) || Array.isArray(coords[1])) {
        // only checking single points for now
        return;
    }

    const dcs = detectCoordinateSystem(coords[1], coords[0]);
    if (dcs !== expectedSys) {
        console.warn("proj sys transform called with possible suspect coords", coords, "expected", expectedSys, "got", dcs);
    }
}


/**
 * Detects the likely coordinate system of a given pair of coordinates.
 * @param {number} x - The first coordinate value, can be latitude or an x (easting) value in projection systems.
 * @param {number} y - The second coordinate value, can be longitude or a y (northing) value in projection systems.
 * @returns {string} - A string indicating the likely coordinate system ('Latitude-Longitude', 'Mercator or Other Projection', or 'Unknown').
 */
function detectCoordinateSystem(x: number, y: number): string {
    // Check if values are within latitude-longitude ranges
    if (x >= -90 && x <= 90 && y >= -180 && y <= 180) {
        return "LATLON";
    }

    // Assuming that for most Mercator or other projections, the coordinate values will exceed the lat-long range,
    // especially for the 'y' coordinate (which can grow significantly in Mercator as you move away from the equator).
    // This is a simplistic check and might not hold for all cases, especially near the projection's origin.
    if (Math.abs(x) > 90 || Math.abs(y) > 180) {
        return "MERCATOR";
    }

    // If the coordinates do not clearly fall into one of the above categories, return 'Unknown'.
    // This could be refined with additional checks for specific projection systems if needed.
    return 'UNKNOWN';
}
